import { CommonModule } from '@angular/common';
import { Component, effect, inject, OnDestroy, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { SystemAdminService } from '../../../../services/system-admin.service';
import { BranchLandingGrids, BranchLandingResponse, CompanyDetailsResponse, CompanyTypeResponse, Model } from '../../../../models/SystemAdmin.model';
import { debounceTime, filter, forkJoin, map, Subject, Subscription, switchMap } from 'rxjs';
import { ToastService } from '../../../../services/toast.service';
import { StateList } from '../../../../models/RequestQuote';
import { ToastType } from '../../../../enums/config.enum';
import { PropertyService } from '../../../../services/property.service';

@Component({
  selector: 'app-branch-list',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './branch-list.component.html',
  styleUrl: './branch-list.component.scss'
})
export class BranchListComponent implements OnDestroy {
  router = inject(Router);
  branchList = signal<BranchLandingGrids[]>([]);
  companyType = signal<CompanyTypeResponse[]>([]);
  currentSortField = signal('Date');
  currentSortDirection = signal('Desc');
  selectedCompany = signal<number>(0);
  selectedState = signal<number>(0);
  searchQuery = signal('');
  searchSubject = new Subject<string>();
  isSearchInputChange = signal<boolean>(false);
  currentFilterList: any = [];
  companyList = signal<CompanyDetailsResponse[]>([]);
  stateList = signal<StateList[]>([]);
  currentPageNumber = signal<number>(1);
  currentPageSize = signal<number>(10);
  displayedPages = signal<number[]>([]);
  totalPages = signal<number>(0);
  jumpToPage = signal<number | null>(null);
  totalItems = signal<number | null>(null);
  startItem = signal<number | null>(null);
  endItem = signal<number | null>(null);
  systemAdminService = inject(SystemAdminService);
  propertyService = inject(PropertyService);
  toastService = inject(ToastService);
  private searchSubscription: Subscription | undefined;

  constructor() {this.setupSearch()}
  ngOnInit(): void {
    this.systemAdminService.removeBranchCountryID() // remove the branch
    this.fetchBranchFilterList();
  }

  fetchAllBranchList(currentPageNumber: number, currentPageSize: number) {
    this.systemAdminService.getAllBranchesList(currentPageNumber, currentPageSize,this.getAddtnlParams()).subscribe({
      next: (response: BranchLandingResponse) => {
        if(response){
          this.branchList.set(response.branchLandingGrids);
          this.onHandleResponseDataBind(response);
        }
      },
      error: (error) => {
        console.log("error", error);
        this.toastService.addNotification({message: 'System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.', type: ToastType.Error});
      }
    })
  }

  fetchBranchFilterList(){
    forkJoin([
      this.systemAdminService.getAllBranchesList(this.currentPageNumber(), this.currentPageSize(), this.getAddtnlParams()),
      this.systemAdminService.getCompanyDropdownList(),
      this.propertyService.getStates(),
      this.systemAdminService.getAllCompanyTypes(),
    ]).pipe(
      map(([branchList, companyList, stateList,companyTypes]) => {
        return {branchList, companyList, stateList,companyTypes};
      })
    ).subscribe({
      next:(response: any) => {
        if(response){
          this.companyList.set(response.companyList);
          this.stateList.set(response.stateList);
          this.branchList.set(response?.branchList?.branchLandingGrids);
          this.companyType.set(response?.companyTypes)
          this.systemAdminService.setCompanyTypeList(response?.companyTypes);
          this.onHandleResponseDataBind(response?.branchList);
        }
      },error: (error: any) => {
        console.log("error", error);
        this.toastService.addNotification({message: 'System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.', type: ToastType.Error});
      }
    })
  }

  setupSearch(): void {
    effect(() => {
      const minChars = 3
      this.searchSubscription = this.searchSubject.pipe(
        debounceTime(300), // Wait for 300ms after the last keypress
        filter(query => {
          const queryLength = query.length;
          if(query.trim() && queryLength >= minChars){return true;}
          else{
            this.currentPageNumber.set(1);
            this.jumpToPage.set(null);
            if(this.isSearchInputChange()){
              this.fetchAllBranchList(this.currentPageNumber(), this.currentPageSize());
              this.isSearchInputChange.set(false);
            }
            return false;
          }
        }), // Only proceed if query has enough characters
        switchMap(query => {
          const searchBy = query.trim();
          this.currentPageNumber.set(1);
          this.jumpToPage.set(null);
          this.isSearchInputChange.set(true);
          return this.systemAdminService.getAllBranchesList(this.currentPageNumber(), this.currentPageSize(), this.getAddtnlParams()) 
        })
      ).subscribe({
        next: (response: any) => {
          if(response){
            this.branchList.set(response.branchLandingGrids);
            this.onHandleResponseDataBind(response);
          }
        },
        error: (error) => {
          console.log("error", error);
        }
      });
    });
  }

  onHandleResponseDataBind(responseData: BranchLandingResponse){
    const {model} = responseData;
    this.currentPageSize.set(model.pageSize);
    this.currentPageNumber.set(model.currentPage);
    this.totalPages.set(model.totalPages);
    this.totalItems.set(model.numOfRecords);
    this.startItem.set((model.currentPage - 1) * model.pageSize + 1);
    const end = model.currentPage * model.pageSize;
    const endRange = end > model.numOfRecords ? model.numOfRecords : end;
    this.endItem.set(endRange);
    this.updateDisplayedPages(model);
  }

  updateDisplayedPages(modelInfo: Model): void {
    const totalPages = modelInfo.totalPages;

    // Calculate the range of pages to display
    const startPage = Math.max(1, modelInfo.currentPage + 1 - 2);
    const endPage = Math.min(totalPages, startPage + 4);

    let pagesToDisplay = [];
    for (let i = startPage; i <= endPage; i++) {
      pagesToDisplay.push(i);
    }
    this.displayedPages.set(pagesToDisplay);
  }

  onSort(field: any, isSortIconUpdated=false): void {
    if (this.currentSortField() === field && isSortIconUpdated) {
      // If the same column is clicked, toggle direction
      this.currentSortDirection.set(this.currentSortDirection() === 'Asc' ? 'Desc' : 'Asc');
    } else if(isSortIconUpdated) {
      // If a new column is clicked, set to ascending by default
      this.currentSortField.set(field);
      this.currentSortDirection.set('Asc');
    }
    const isJumpToPageActive = this.jumpToPage() ? true : false;
    this.onPageSeleted(this.currentPageNumber(), isJumpToPageActive);
  }

  getAddtnlParams(): { [key: string]: string | number | null }{
    const sortParams: { [key: string]: string | number | null } = {sort: this.currentSortDirection(), sortColumn: this.currentSortField()};
    this.selectedCompany() ? sortParams['company'] = this.selectedCompany() : '';
    this.selectedState() !== 0 ? sortParams['state'] = this.selectedState() : '';
    if(this.searchQuery() && this.searchQuery().length >= 3){
      sortParams['search'] = this.searchQuery();
    }
    return sortParams;
  }

  onJumpToChange(event: Event): void{
    const input = event.target as HTMLInputElement;
    if (input.value !== '') {
      this.jumpToPage.set(Number(input.value))
    }else{
      this.jumpToPage.set(1); 
    }
  }

  // Method to jump to the specific page
  jumpToSpecificPage() {
    const page = this.jumpToPage();
    if(!this.jumpToPage()){
      return;
    }

    if (page && page >= 1 && page <= this.totalPages()) {
      this.currentPageNumber.set(page);
    } else {
      this.jumpToPage.set(1);
      this.currentPageNumber.set(1);
    }

    this.onPageSeleted(this.currentPageNumber(), true);
  }

  // Validation method to allow only numeric values
  allowOnlyNumbers(event: KeyboardEvent) {
    const charCode = event.key.charCodeAt(0);
    
    // Check if the input is a number or control keys (backspace, delete, etc.)
    if (charCode < 48 || charCode > 57) {
      event.preventDefault(); // Prevent non-numeric input
    }
  }

  
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery.set(input.value);
    // Emit the new value to the subject
    this.searchSubject.next(input.value);
  }

  onClearSearch() {
    this.searchQuery.set('');
    // Emit the new value to the subject
    this.searchSubject.next('');
  } 

  onPageSeleted(currentPage: number, isJumpToUserUpdate: boolean = false){
    this.fetchAllBranchList(currentPage, this.currentPageSize());
    if (!isJumpToUserUpdate) this.jumpToPage.set(null);
  }

  onCompanyChange(event: Event) {
    const selectedCompany = event.target as HTMLSelectElement;
    this.currentPageNumber.set(1);
    this.jumpToPage.set(null);
    this.selectedCompany.set(Number(selectedCompany.value));
    let removeExistingCompanyFilter = this.currentFilterList.filter((item: any) => item.type !== "company");
    let selectedCompanyName = this.companyList().filter((company) => String(company.companyId) === selectedCompany.value);
    if(selectedCompany.value !== "0"){
      removeExistingCompanyFilter.push({
        type: 'company', name: selectedCompanyName[0].companyName
      }); 
      this.currentFilterList = removeExistingCompanyFilter;
      this.fetchAllBranchList(this.currentPageNumber(), this.currentPageSize());
    }else{
      this.currentFilterList = removeExistingCompanyFilter;
      this.selectedCompany.set(0);
      this.fetchAllBranchList(this.currentPageNumber(), this.currentPageSize());
    }
  }

  onStateChange(event: Event) {
    const selectedState = event.target as HTMLSelectElement;
    this.currentPageNumber.set(1);
    this.jumpToPage.set(null);
    this.selectedState.set(Number(selectedState.value));
    let removeExistingStateFilter = this.currentFilterList.filter((item: any) => item.type !== "state");
    let selectedStateName = this.stateList().filter((state) => String(state.stateId) === selectedState.value);
    if(selectedState.value !== "0"){
      removeExistingStateFilter.push({
        type: 'state', name: selectedStateName[0].stateName
      }); 
      this.currentFilterList = removeExistingStateFilter;
      this.fetchAllBranchList(this.currentPageNumber(), this.currentPageSize());
    }else{
      this.currentFilterList = removeExistingStateFilter;
      this.selectedState.set(0);
      this.fetchAllBranchList(this.currentPageNumber(), this.currentPageSize());
    }
  }

  onClearCurrentFilter(type:string){
    let removeExistingFilter = this.currentFilterList.filter((item: any) => item.type !== type);
    switch(type){
      case 'company':
        this.selectedCompany.set(0);
        break;
      case 'state':
        this.selectedState.set(0);
        break;
    }
    this.currentFilterList = removeExistingFilter;
    this.fetchAllBranchList(1, this.currentPageSize());
  }

  onClearAllfilter(){
    this.currentFilterList = [];
    this.selectedCompany.set(0);
    this.selectedState.set(0);
    this.fetchAllBranchList(1, this.currentPageSize());
  }

  addBranch(){
    this.router.navigate(['/system-admin-dashboard/branch/add-branch'])
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();  // Clean up subscription
    }
  }

}
